import type { ContentRiskLevel } from "../../types/aiSuggestion";
import { type StripFlagBand } from "../atoms/FilledFlagIcon";
import { FlagIcon } from "../atoms/FlagIcon";

export type RiskSummaryPillProps = {
  readonly activeLevel: ContentRiskLevel;
  readonly onSelectLevel: (level: ContentRiskLevel) => void;
  readonly pillRedPrimary: string;
  readonly pillAmberPrimary: string;
  readonly pillGreenPrimary: string;
  readonly isLoading: boolean;
  /** Risk tiers to show as selectable pills (all tiers for browse; action gating is elsewhere). */
  readonly allowedLevels: ReadonlySet<ContentRiskLevel>;
};

const ROWS: readonly {
  readonly level: ContentRiskLevel;
  readonly band: StripFlagBand;
  readonly label: "Red" | "Amber" | "Green";
}[] = [
  { level: "LegalDecision", band: "red", label: "Red" },
  { level: "BusinessDecision", band: "amber", label: "Amber" },
  { level: "ExecDecision", band: "green", label: "Green" },
];

function primaryCountForLevel(
  level: ContentRiskLevel,
  primaries: { red: string; amber: string; green: string },
): string {
  switch (level) {
    case "LegalDecision":
      return primaries.red;
    case "BusinessDecision":
      return primaries.amber;
    case "ExecDecision":
      return primaries.green;
  }
}

export const RiskSummaryPill = ({
  activeLevel,
  onSelectLevel,
  pillRedPrimary,
  pillAmberPrimary,
  pillGreenPrimary,
  isLoading,
  allowedLevels,
}: RiskSummaryPillProps) => {
  const primaries = {
    red: pillRedPrimary,
    amber: pillAmberPrimary,
    green: pillGreenPrimary,
  } as const;

  const visibleRows = ROWS.filter((row) => allowedLevels.has(row.level));

  return (
    <fieldset
      className="inline-flex max-w-full flex-wrap items-center gap-2 border-0 p-0"
      aria-label="Filter suggestions by risk category"
    >
      {visibleRows.map(({ level, band, label }) => {
        const active = activeLevel === level;
        const showLoading = isLoading && active;

        return (
          <button
            key={level}
            type="button"
            className={`inline-flex items-center gap-2 rounded px-2 py-1 text-sm transition-all duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${
              active ? "bg-white/70" : "hover:bg-white/40"
            }`}
            onClick={() => onSelectLevel(level)}
            aria-pressed={active}
            disabled={isLoading}
          >
            <FlagIcon band={band} />
            <span
              className={`text-[13px] text-gray-900 ${active ? "font-semibold" : "font-medium"}`}
            >
              {showLoading ? "..." : primaryCountForLevel(level, primaries)}
            </span>
            <span className="text-xs text-gray-600">{label}</span>
          </button>
        );
      })}
    </fieldset>
  );
};
